import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import { Alert, Chip, Skeleton, Stack, Typography } from "@mui/material"
import { ReactElement, useMemo, useState } from "react"

import ApproveRejectDialog from "./approveRejectDialog"
import {
    ChangeAccordion,
    ChangeAccordionDetails,
    ChangeAccordionSummary,
} from "./changeAccordion"
import ChangeRequestMeta from "./changeRequestMeta"
import ChangeSummaryGrids from "./changeSummaryGrids"
import InitiateChangeAccordion from "./initiateChangeAccordion"
import DataLoadError from "../../../components/fallback/dataLoadError"
import { useScreenSize } from "../../../hooks/useScreenSize"
import useGetChangeRequests from "../../../services/middleware/hooks/dnoThreshold/useGetChangeRequests"
import { IChangeRequest } from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"
import { formatDate } from "../../../utils/general/generalUtils"

type ConfigurationChangesProps = {
    isThresholdManager: boolean
    username: string
    buttonSlotEl: HTMLDivElement | null
}

const STATUS_CHIP_MAP: Record<
    string,
    {
        label: string
        color: "success" | "warning" | "error"
        icon: ReactElement
    }
> = {
    APPROVED: {
        label: "Approved",
        color: "success",
        icon: <CheckCircleOutlineIcon />,
    },
    PENDING_APPROVAL: {
        label: "Pending",
        color: "warning",
        icon: <HourglassEmptyIcon />,
    },
    REJECTED: {
        label: "Rejected",
        color: "error",
        icon: <ErrorOutlineIcon />,
    },
}

/**
 * Renders a status chip for a change request.
 */
const StatusChip = ({ status }: { status: string }): ReactElement => {
    const chip = STATUS_CHIP_MAP[status]
    return chip ? (
        <Chip
            label={chip.label}
            color={chip.color}
            size="small"
            icon={chip.icon}
            variant="filled"
        />
    ) : (
        <Chip label={status} size="small" variant="filled" />
    )
}

/**
 * Tab 2: Configuration Changes — accordion view showing
 * change request history with inline details and review.
 */
const ConfigurationChanges = ({
    isThresholdManager,
    username,
    buttonSlotEl,
}: ConfigurationChangesProps): ReactElement => {
    const { isTabletOrMob } = useScreenSize()
    const { data, error, isLoading, mutate } = useGetChangeRequests()
    const [reviewTarget, setReviewTarget] = useState<IChangeRequest | null>(
        null
    )

    const changeRequests = useMemo(() => data?.changeRequests ?? [], [data])

    const hasPendingChange = useMemo(
        () => changeRequests.some((cr) => cr.status === "PENDING_APPROVAL"),
        [changeRequests]
    )
    const canReview = (cr: IChangeRequest): boolean =>
        isThresholdManager && cr.status === "PENDING_APPROVAL"
    if (error) return <DataLoadError />
    if (isLoading) {
        return <Skeleton variant="rectangular" height={400} />
    }

    return (
        <>
            {isThresholdManager ? (
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Configuration changes require two approvers. To begin, click{" "}
                    <strong>Initiate Change</strong> — this acquires a 10-minute
                    editing lock so only one person can edit at a time. After
                    submitting, a different Threshold Manager must approve or
                    reject the change before it takes effect.
                </Alert>
            ) : (
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    This page shows the history of changes to your DNO
                    configuration and thresholds. If you have any questions
                    about a change, please contact your Threshold Manager.
                </Alert>
            )}
            <InitiateChangeAccordion
                isThresholdManager={isThresholdManager}
                username={username}
                buttonSlotEl={buttonSlotEl}
                hasPendingChange={hasPendingChange}
                onSubmitSuccess={(): void => {
                    mutate()
                }}
            />

            {changeRequests.length === 0 && (
                <Typography
                    color="text.secondary"
                    sx={{ py: 4, textAlign: "center" }}
                >
                    No configuration changes have been submitted yet.
                </Typography>
            )}

            {changeRequests.length > 0 && (
                <Stack spacing={2}>
                    {changeRequests.map((cr) => (
                        <ChangeAccordion key={cr.changeRequestId}>
                            <ChangeAccordionSummary>
                                <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row",
                                    }}
                                    spacing={1}
                                    alignItems={{
                                        xs: "flex-start",
                                        sm: "center",
                                    }}
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        fontWeight={600}
                                        fontSize={20}
                                    >
                                        {" "}
                                        {formatDate(cr.raisedAt)}
                                    </Typography>
                                    <Typography
                                        fontWeight={400}
                                        fontSize={20}
                                        variant="body2"
                                        color="text.secondary"
                                        noWrap={!isTabletOrMob}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        — {cr.title}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{
                                            ml: {
                                                xs: 0,
                                                sm: "auto !important",
                                            },
                                        }}
                                        flexWrap="wrap"
                                        useFlexGap
                                    >
                                        <Stack spacing={1}>
                                            <Chip
                                                icon={<PersonOutlineIcon />}
                                                label={
                                                    cr.raisedByName || "Unknown"
                                                }
                                                size="small"
                                                variant="outlined"
                                            />
                                            {cr.reviewedBy && (
                                                <Chip
                                                    icon={
                                                        <CheckCircleOutlineIcon />
                                                    }
                                                    label={
                                                        cr.reviewedByName ||
                                                        "Unknown"
                                                    }
                                                    size="small"
                                                    variant="filled"
                                                />
                                            )}
                                        </Stack>
                                        <StatusChip status={cr.status} />
                                    </Stack>
                                </Stack>
                            </ChangeAccordionSummary>
                            <ChangeAccordionDetails>
                                <ChangeRequestMeta
                                    changeRequest={cr}
                                    canReview={canReview(cr)}
                                    username={username}
                                    onReviewClick={(): void =>
                                        setReviewTarget(cr)
                                    }
                                />

                                <ChangeSummaryGrids changes={cr.changes} />
                            </ChangeAccordionDetails>
                        </ChangeAccordion>
                    ))}
                </Stack>
            )}

            {reviewTarget && (
                <ApproveRejectDialog
                    open={!!reviewTarget}
                    changeRequest={reviewTarget}
                    onClose={(): void => setReviewTarget(null)}
                    onActionSuccess={(): void => {
                        setReviewTarget(null)
                        mutate()
                    }}
                />
            )}
        </>
    )
}

export default ConfigurationChanges
